import { useState, useEffect } from "react";
import api from "../../axiosconfig";
import { toast } from "react-toastify";

const defaultSummaries = {
    physical: "",
    emotional: "",
    acceptance: "",
    relationships: "",
    loving_kindness: "",
    intellectual: "",
};

/**
 * Hook to fetch AI-generated organizational summaries from assessment averages
 * @param {string} viewMode - "current" or "range"
 * @param {object} averages - Averaged assessment scores per domain
 * @param {boolean} loadingAverages - Whether averages are still loading
 * @returns {object} { summaries, generalSummary, loading }
 */
export default function useAssessmentAiSummary(viewMode, averages, loadingAverages) {
    const [summaries, setSummaries] = useState(defaultSummaries);
    const [generalSummary, setGeneralSummary] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Only fetch in current mode
        if (viewMode !== "current") return;
        
        // Don't fetch while averages are loading
        if (loadingAverages) return;

        const fetchAiSummary = async () => {
            try {
                setLoading(true);

                // Check if we have valid averages (now they're at top level of averages object)
                // Must be numbers AND greater than 0
                const hasValidData = averages && Object.values(averages).some(val => 
                    typeof val === 'number' && val > 0
                );

                if (!hasValidData) {
                    console.log("[useAssessmentAiSummary] Skipping AI analysis - no valid average data (all null/zero)", averages);
                    setSummaries(defaultSummaries);
                    setGeneralSummary("");
                    return;
                }

                console.log("[useAssessmentAiSummary] Calling analyzeOrg with averages:", averages);

                // Call the analyzeOrg endpoint with the averages
                const res = await api.post("/api/assessments/analyzeOrg", {
                    averages: {
                        physical: averages.physical ?? null,
                        emotional: averages.emotional ?? null,
                        acceptance: averages.acceptance ?? null,
                        relationships: averages.relationships ?? null,
                        loving_kindness: averages.loving_kindness ?? null,
                        intellectual: averages.intellectual ?? null,
                    },
                });

                const data = res?.data?.data;
                if (!data) {
                    setSummaries(defaultSummaries);
                    setGeneralSummary("");
                    return;
                }

                // Extract domain summaries and general summary
                setSummaries({
                    physical: data.PhysicalSummary || "",
                    emotional: data.EmotionalSummary || "",
                    acceptance: data.AcceptanceSummary || "",
                    relationships: data.RelationshipSummary || "",
                    loving_kindness: data.LovingKindnessSummary || "",
                    intellectual: data.IntellectualSummary || "",
                });

                setGeneralSummary(data.GeneralSummary || "");
            } catch (error) {
                console.error("[useAssessmentAiSummary] Error fetching AI analysis:", error);
                
                // Don't show error toast if it's a validation error (all averages null/zero)
                const isValidationError = error?.response?.data?.error?.includes("all averages are null or zero");
                
                if (!isValidationError) {
                    toast.error("Failed to load AI analysis.");
                }
                
                setSummaries(defaultSummaries);
                setGeneralSummary("");
            } finally {
                setLoading(false);
            }
        };

        fetchAiSummary();
    }, [averages, viewMode, loadingAverages]);

    return { summaries, generalSummary, loading };
}
